import { checkAmount } from "./moneyInput";
import { inferKind, planBudget, monthlyAmount, type Kind, type Period, type BudgetPlan } from "./budgetPlan";

export { inferKind, type Kind, type Period };
export interface Line { id: number; name: string; amount: string; kind?: Kind; /** Rows saved before B-0136 have no period: they are monthly. */ period?: Period }

export const kindOf = (l: Line): Kind => l.kind ?? inferKind(l.name);
export const periodOf = (l: Line): Period => l.period ?? "month";

export const KIND_LABEL: Record<Kind, string> = { need: "Need", want: "Want", savings: "Savings", debt: "Debt payment" };

export const DEFAULT_INCOME: Line[] = [{ id: 1, name: "Salary", amount: "3500" }];
export const DEFAULT_EXPENSES: Line[] = [
  { id: 1, name: "Rent / Mortgage", amount: "1200", kind: "need" },
  { id: 2, name: "Groceries", amount: "400", kind: "need" },
  { id: 3, name: "Utilities", amount: "150", kind: "need" },
  { id: 4, name: "Transportation", amount: "200", kind: "need" },
  { id: 5, name: "Insurance", amount: "150", kind: "need" },
  { id: 6, name: "Subscriptions", amount: "50", kind: "want" },
  { id: 7, name: "Savings", amount: "300", kind: "savings" },
];

/** One error message per row id (rows with a readable, non-negative amount are absent). */
export function lineErrors(lines: Line[]): Record<number, string> {
  const out: Record<number, string> = {};
  for (const l of lines) {
    const c = checkAmount(l.amount, { label: l.name.trim() || "Amount" });
    if (c.value === null) out[l.id] = c.error;
  }
  return out;
}

export type Summary = Pick<BudgetPlan, "income" | "needs" | "wants" | "saved" | "debt" | "yearlySetAside" | "spending" | "leftOver">;

/** The monthly amount of a row (a yearly row is amount / 12). Only call it for a row with no error. */
export const monthlyOf = (l: Line): number => monthlyAmount(checkAmount(l.amount).value ?? 0, periodOf(l));

/** Totals, or null when any row has an error (never silently counts a bad amount as 0). The sums come from budgetPlan.ts, which the API shares. */
export function summarize(income: Line[], expenses: Line[]): Summary | null {
  if (Object.keys(lineErrors(income)).length || Object.keys(lineErrors(expenses)).length) return null;
  const amt = (l: Line) => checkAmount(l.amount).value ?? 0;
  const inc = income.reduce((s, l) => s + amt(l), 0);
  const p = planBudget(inc, expenses.map((l) => ({ name: l.name, amount: amt(l), kind: kindOf(l), period: periodOf(l) })));
  return { income: p.income, needs: p.needs, wants: p.wants, saved: p.saved, debt: p.debt, yearlySetAside: p.yearlySetAside, spending: p.spending, leftOver: p.leftOver };
}

/** Share of income as a percent (null when there is no income to compare against). */
export const pctOfIncome = (n: number, income: number): number | null => (income > 0 ? (n / income) * 100 : null);

export const RULE_50_30_20: { kind: Kind; label: string; target: number }[] = [
  { kind: "need", label: "Needs", target: 50 },
  { kind: "want", label: "Wants", target: 30 },
  { kind: "savings", label: "Savings", target: 20 },
];

export function budgetCsvRows(income: Line[], expenses: Line[], s: Summary): (string | number)[][] {
  const amt = (l: Line) => checkAmount(l.amount).value ?? 0;
  const pct = (n: number) => { const p = pctOfIncome(n, s.income); return p === null ? "" : p.toFixed(1); };
  // The Amount column is always the monthly amount, so the rows add up to the totals. Rows entered per year add two columns: what was entered, and the period.
  const yearly = expenses.some((l) => periodOf(l) === "year");
  const extra = (e: string | number, per: string): (string | number)[] => (yearly ? [e, per] : []);
  return [
    ["Type", "Name", "Category", "Amount", "% of income", ...(yearly ? ["Entered amount", "Entered per"] : [])],
    ...income.map((l) => ["Income", l.name, "", amt(l).toFixed(2), "", ...extra(amt(l).toFixed(2), "month")]),
    ...expenses.map((l) => ["Expense", l.name, KIND_LABEL[kindOf(l)], monthlyOf(l).toFixed(2), pct(monthlyOf(l)), ...extra(amt(l).toFixed(2), periodOf(l))]),
    ["Total", "Income", "", s.income.toFixed(2), "", ...extra("", "")],
    ["Total", "Expenses (not savings)", "", s.spending.toFixed(2), pct(s.spending), ...extra("", "")],
    ["Total", "Saved", "", s.saved.toFixed(2), pct(s.saved), ...extra("", "")],
    ["Total", "Left over", "", s.leftOver.toFixed(2), pct(s.leftOver), ...extra("", "")],
  ];
}
